import Chip from "@mui/material/Chip";
import type { ChipProps } from "@mui/material/Chip";
import { Stack, Tooltip, Typography } from "@mui/material";
import { CustomCellDate, CrudLink } from "@/shared/components";
import type { ColumnDefinition } from "@/shared/components/CRUD/types";
import type { DataListViewColumns } from "@/shared/components/DataListView";
import { PagePaths } from "@/admin/shared/constants";
import { formatUsd } from "@/admin/shared/utils";
import type { RefundRow } from "./types";

const statusChipConfig: Record<
  string,
  { label: string; color: ChipProps["color"] }
> = {
  PENDING: { label: "В обработке", color: "warning" },
  SUCCEEDED: { label: "Выполнен", color: "success" },
  FAILED: { label: "Ошибка", color: "error" },
};

// Тот же локальный маппинг типа продукта, что и в feature productPurchases —
// admin не переиспользует partner-локализацию, здесь нет i18n.
const typeLabel: Record<string, string> = {
  PPV: "Разовая покупка",
  SUBSCRIPTION: "Подписка",
};

// Ручной возврат (старые платежи Paynet): учтён только у нас, провайдер
// его не получал, деньги партнёр возвращает у провайдера сам.
const MANUAL_REFUND_TOOLTIP =
  "Возврат учтён только у нас, провайдер его не получал: деньги покупателю возвращаются вручную в кабинете провайдера";

function renderStatus(refund: Pick<RefundRow, "status" | "manual">) {
  if (refund.manual) {
    return (
      <Tooltip title={MANUAL_REFUND_TOOLTIP}>
        <Chip label="Вернуть вручную" color="warning" size="small" />
      </Tooltip>
    );
  }
  const { label, color } = statusChipConfig[refund.status] ?? {
    label: refund.status,
    color: "default" as const,
  };
  return <Chip label={label} color={color} size="small" />;
}

function renderBuyer(refund: RefundRow) {
  const { payment } = refund;
  if (payment?.userId) {
    return (
      <CrudLink path={PagePaths.users} tab="details" id={payment.userId}>
        {payment.buyerEmail ?? "—"}
      </CrudLink>
    );
  }
  return payment?.buyerEmail ?? "—";
}

export const getColumnsConfig = (): ColumnDefinition<RefundRow>[] => [
  { name: "id", label: "ID", options: { display: false, filter: false } },
  {
    name: "amount",
    label: "Сумма",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const amount = data as RefundRow["amount"];
        return `${amount.value} ${amount.currency}`;
      },
    },
  },
  {
    name: "amountUSD",
    label: "USD",
    options: {
      sort: false,
      customBodyRender: (value: unknown) =>
        formatUsd(value as number | null | undefined),
    },
  },
  {
    name: "ottkitFeeUSD",
    label: "Комиссия USD",
    options: {
      sort: false,
      customBodyRender: (value: unknown) =>
        formatUsd(value as number | null | undefined),
    },
  },
  {
    name: "organizationUSD",
    label: "Доход организации USD",
    options: {
      sort: false,
      customBodyRender: (value: unknown) =>
        formatUsd(value as number | null | undefined),
    },
  },
  {
    name: "buyer",
    label: "Покупатель",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => renderBuyer(data as RefundRow),
    },
  },
  {
    name: "product",
    label: "Продукт",
    options: {
      sort: false,
      // Продукты admin-only не редактируются (нет админ-поверхности
      // управления продуктами) — ссылки нет, только текст.
      customBodyRender: (data: unknown) => {
        const refund = data as RefundRow;
        return refund.payment?.product?.title ?? "—";
      },
    },
  },
  {
    name: "provider",
    label: "Провайдер",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const refund = data as RefundRow;
        return refund.payment?.provider ?? "—";
      },
    },
  },
  {
    name: "type",
    label: "Тип",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const refund = data as RefundRow;
        const type = refund.payment?.product?.type;
        if (!type) return "—";
        return typeLabel[type] ?? type;
      },
    },
  },
  {
    name: "status",
    label: "Статус",
    options: {
      customBodyRender: (value: unknown) => renderStatus(value as RefundRow),
    },
  },
  {
    name: "organization",
    label: "Организация",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const refund = data as RefundRow;
        return (
          <CrudLink
            path={PagePaths.organizations}
            tab="details"
            id={refund.organizationId}
          >
            {refund.organization?.title ?? "—"}
          </CrudLink>
        );
      },
    },
  },
  {
    name: "createdAt",
    label: "Дата",
    options: {
      setCellHeaderProps: () => ({ style: { width: 150 } }),
      customBodyRender: (value: unknown) => (
        <CustomCellDate date={value as string} />
      ),
    },
  },
];

export const getTextsConfig = () => ({
  filterTitle: "Фильтры",
  listTitle: "Возвраты",
  refetchList: "Обновить список",
});

export const getMobileColumnsConfig = (): DataListViewColumns<RefundRow> => ({
  main: {
    title: "Возврат",
    sx: { flex: 1, minWidth: 0 },
    renderCell: (row) => (
      <Stack sx={{ minWidth: 0 }}>
        <Typography noWrap>
          {row.amount.value} {row.amount.currency}
        </Typography>
        <Typography variant="body2" color="textSecondary" noWrap>
          {row.organization?.title ?? "—"}
        </Typography>
      </Stack>
    ),
  },
  status: {
    title: "Статус",
    sx: { width: 132, flexShrink: 0 },
    renderCell: (row) => renderStatus(row),
  },
});
